/**
 * StorePage.jsx — Premium tenant parts store (revamp).
 *
 * Search + category filter + premium grid + sticky WhatsApp inquiry cart.
 * Wrapped in TenantThemeProvider + TenantLayout.
 * Legacy preserved at /:garageSlug/legacy/store via StorePageLegacy.jsx.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    Package, Search, AlertTriangle, ShoppingBag, X, MessageCircle,
    Plus, Minus, BadgeCheck, Tag, Droplets, CircleDot, Disc, Link as LinkIcon,
    BatteryCharging, Lightbulb, Cog, Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { TenantThemeProvider } from '../components/tenant/TenantThemeContext';
import TenantLayout from '../components/tenant/TenantLayout';
import TenantSEO from '../components/tenant/TenantSEO';
import TenantLoader from '../components/tenant/ui/TenantLoader';
import {
    GlassCard, TiltCard, RevealOnScroll, AmbientBlob, GridBackdrop, MagneticButton
} from '../components/tenant/ui/primitives';
import { FinalCTA } from '../components/tenant/sections/Closing';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
    engine_oil: { en: 'Engine Oil', bn: 'ইঞ্জিন অয়েল' },
    tires: { en: 'Tires', bn: 'টায়ার' },
    brakes: { en: 'Brakes', bn: 'ব্রেক' },
    chain: { en: 'Chain & Sprocket', bn: 'চেইন' },
    battery: { en: 'Battery', bn: 'ব্যাটারি' },
    lights: { en: 'Lights', bn: 'লাইট' },
    accessories: { en: 'Accessories', bn: 'অ্যাকসেসরিজ' }
};

const CATEGORY_ICON = {
    engine_oil: Droplets,
    tires: CircleDot,
    brakes: Disc,
    chain: LinkIcon,
    battery: BatteryCharging,
    lights: Lightbulb,
    accessories: Cog
};

// Robust price resolver — backend uses `selling_price`, legacy seeds may use `price`.
function partPrice(p) {
    if (!p) return 0;
    const v = p.selling_price ?? p.price ?? p.mrp ?? 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

// Display a price RANGE rather than an exact value — keeps quoting flexible
// while still anchoring the customer's expectation. Spread shrinks as the
// absolute price grows so high-priced items don't show wild swings.
function priceRangeText(p, language) {
    const v = partPrice(p);
    if (!v) return null;
    // Tighter spread on bigger ticket items.
    const spread = v < 500 ? 0.10 : v < 2000 ? 0.07 : v < 10000 ? 0.05 : 0.03;
    const step = v < 200 ? 10 : v < 1000 ? 25 : v < 5000 ? 50 : v < 20000 ? 100 : 250;
    const round = (n) => Math.max(step, Math.round(n / step) * step);
    const lo = round(v * (1 - spread));
    const hi = round(v * (1 + spread));
    if (lo === hi) return formatCurrency(lo, language);
    return `${formatCurrency(lo, language)} – ${formatCurrency(hi, language)}`;
}

// Robust icon resolver — handles arbitrary DB strings like "Engine Oil", "tyre", "brake-pad", etc.
function pickCategoryIcon(raw) {
    const key = String(raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (CATEGORY_ICON[key]) return CATEGORY_ICON[key];
    if (/oil|lubric|grease|coolant|fluid/.test(key)) return Droplets;
    if (/tyre|tire|wheel|rim/.test(key)) return CircleDot;
    if (/brake|pad|disc|rotor/.test(key)) return Disc;
    if (/chain|sprocket|belt/.test(key)) return LinkIcon;
    if (/batter|electric|spark|plug/.test(key)) return BatteryCharging;
    if (/light|bulb|lamp|head|tail|indicator/.test(key)) return Lightbulb;
    if (/tool|wrench|spanner/.test(key)) return Wrench;
    if (/access|grip|mirror|cover|seat/.test(key)) return Package;
    return Cog;
}

export default function StorePage() {
    const { garageSlug } = useParams();
    const { language } = useLanguage();
    const [tenant, setTenant] = useState(null);
    const [parts, setParts] = useState([]);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [cart, setCart] = useState({}); // { partId: qty }
    const [cartOpen, setCartOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!garageSlug) return;
        let cancelled = false;
        const siteReq = axios.get(`${API}/tenants/${garageSlug}/site`)
            .catch(() => axios.get(`${API}/tenants/${garageSlug}/public`));
        const partsReq = axios.get(`${API}/tenants/${garageSlug}/parts`).catch(() => ({ data: [] }));

        Promise.all([siteReq, partsReq])
            .then(([t, p]) => {
                if (cancelled) return;
                setTenant(t.data);
                setParts(Array.isArray(p.data) ? p.data : []);
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 404) setNotFound(true);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [garageSlug]);

    const cats = useMemo(() => {
        const set = new Set();
        parts.forEach(p => p.category && set.add(p.category));
        return [{ value: 'all', en: 'All', bn: 'সব' },
        ...Array.from(set).map(v => ({
            value: v,
            en: CATEGORY_LABELS[v]?.en || v,
            bn: CATEGORY_LABELS[v]?.bn || v
        }))];
    }, [parts]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return parts.filter(p => {
            const catOk = category === 'all' || p.category === category;
            const qOk = !q
                || p.name?.toLowerCase().includes(q)
                || p.name_bn?.includes(search)
                || p.brand?.toLowerCase().includes(q)
                || p.sku?.toLowerCase().includes(q);
            return catOk && qOk;
        });
    }, [parts, category, search]);

    const cartItems = useMemo(() => {
        return Object.entries(cart)
            .map(([id, qty]) => ({ part: parts.find(p => p.id === id), qty }))
            .filter(x => x.part);
    }, [cart, parts]);
    const cartCount = cartItems.reduce((sum, x) => sum + x.qty, 0);
    const cartTotal = cartItems.reduce((sum, x) => sum + partPrice(x.part) * x.qty, 0);

    function addToCart(id) { setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 })); }
    function decCart(id) {
        setCart(c => {
            const n = (c[id] || 0) - 1;
            const next = { ...c };
            if (n <= 0) delete next[id]; else next[id] = n;
            return next;
        });
    }
    function clearCart() { setCart({}); }

    function buildWhatsAppMessage() {
        const lines = [
            language === 'bn' ? 'হ্যালো! আমি নিচের পার্টস সম্পর্কে জানতে চাই:' : 'Hi! I\'d like to inquire about these parts:',
            ''
        ];
        cartItems.forEach(({ part, qty }) => {
            const name = (language === 'bn' && part.name_bn) ? part.name_bn : part.name;
            const unit = partPrice(part);
            const priceTag = unit > 0 ? ` — ${formatCurrency(unit * qty, language)}` : '';
            lines.push(`• ${name}${part.sku ? ` (${part.sku})` : ''} × ${qty}${priceTag}`);
        });
        lines.push('');
        lines.push(language === 'bn' ? `মোট: ${formatCurrency(cartTotal, 'bn')}` : `Estimated total: ${formatCurrency(cartTotal, 'en')}`);
        return encodeURIComponent(lines.join('\n'));
    }

    if (loading) return <Loading slug={garageSlug} />;
    if (notFound || !tenant) return <NotFound slug={garageSlug} />;

    const whatsapp = tenant.whatsapp_number || tenant.phone;

    return (
        <TenantThemeProvider tenant={tenant}>
            <TenantSEO tenant={tenant} slug={garageSlug} page="store" />
            <TenantLayout>
                <StoreHero count={parts.length} />
                <StoreFilters
                    search={search}
                    onSearch={setSearch}
                    category={category}
                    onCategory={setCategory}
                    cats={cats}
                    language={language}
                />
                <PartsGrid
                    parts={filtered}
                    cart={cart}
                    onAdd={addToCart}
                    onDec={decCart}
                    language={language}
                    onJumpToRequest={() => {
                        const el = document.getElementById('parts-request');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                />
                <CustomPartsRequest slug={garageSlug} language={language} />
                <FinalCTA />

                {/* Floating "Custom Parts Request" rail — mid-right, vertical */}
                <CustomPartsPill language={language} />

                {/* Sticky inquiry cart trigger */}
                <AnimatePresence>
                    {cartCount > 0 && !cartOpen && (
                        <motion.button
                            key="cart-fab"
                            initial={{ y: 80, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 80, opacity: 0 }}
                            onClick={() => setCartOpen(true)}
                            className="fixed bottom-24 right-5 md:bottom-8 md:right-8 z-40 inline-flex items-center gap-3 pl-3 pr-5 h-14 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] shadow-2xl shadow-[var(--tenant-primary)]/40 font-semibold uppercase tracking-wide text-sm hover:scale-[1.02] transition"
                        >
                            <span className="w-9 h-9 rounded-full bg-black/20 flex items-center justify-center text-base">{cartCount}</span>
                            {language === 'bn' ? 'ইনকোয়ারি দেখুন' : 'View inquiry'}
                        </motion.button>
                    )}
                </AnimatePresence>

                <CartDrawer
                    open={cartOpen}
                    onClose={() => setCartOpen(false)}
                    items={cartItems}
                    total={cartTotal}
                    onAdd={addToCart}
                    onDec={decCart}
                    onClear={clearCart}
                    whatsapp={whatsapp}
                    buildMessage={buildWhatsAppMessage}
                    language={language}
                />
            </TenantLayout>
        </TenantThemeProvider>
    );
}

function StoreHero({ count }) {
    const { language } = useLanguage();
    return (
        <section className="relative pt-24 md:pt-28 pb-10 overflow-hidden">
            <AmbientBlob className="-top-32 left-1/4" size={580} />
            <GridBackdrop opacity={0.05} />
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <RevealOnScroll>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--tenant-border)] bg-[var(--tenant-surface)]/60 backdrop-blur text-xs uppercase tracking-[0.28em] text-[var(--tenant-muted)]">
                        <Package className="w-3.5 h-3.5 text-[var(--tenant-primary)]" />
                        {language === 'bn' ? 'পার্টস স্টোর' : 'Parts store'}
                    </div>
                    <h1 className={['mt-6 font-display text-6xl md:text-8xl tracking-wider text-[var(--tenant-ink)]', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                        {language === 'bn' ? 'অরিজিনাল পার্টস' : 'Genuine parts.'}
                    </h1>
                    <p className="mt-4 text-lg text-[var(--tenant-muted)] max-w-2xl">
                        {language === 'bn'
                            ? `${count}+ আইটেম স্টকে — ইনকোয়ারি পাঠান, আমরা WhatsApp-এ দাম নিশ্চিত করব।`
                            : `${count}+ items in stock. Add to inquiry — we'll confirm price and availability on WhatsApp.`}
                    </p>
                </RevealOnScroll>
            </div>
        </section>
    );
}

function StoreFilters({ search, onSearch, category, onCategory, cats, language }) {
    return (
        <div className="sticky top-20 z-20 backdrop-blur-md bg-[var(--tenant-surface)]/70 border-y border-[var(--tenant-border)]">
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-2 px-3 h-11 rounded-full border border-[var(--tenant-border)] bg-[var(--tenant-surface)] md:w-80">
                    <Search className="w-4 h-4 text-[var(--tenant-muted)]" />
                    <input
                        type="search"
                        placeholder={language === 'bn' ? 'নাম, ব্র্যান্ড বা SKU…' : 'Name, brand, or SKU…'}
                        value={search}
                        onChange={e => onSearch(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-sm text-[var(--tenant-ink)]"
                    />
                </div>
                <div className="flex-1 overflow-x-auto -mx-1 scrollbar-none">
                    <div className="inline-flex gap-2 px-1">
                        {cats.map(c => (
                            <button
                                key={c.value}
                                onClick={() => onCategory(c.value)}
                                className={[
                                    'h-9 px-4 rounded-full text-xs uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition border',
                                    category === c.value
                                        ? 'bg-[var(--tenant-primary)] text-[var(--tenant-surface)] border-transparent'
                                        : 'border-[var(--tenant-border)] text-[var(--tenant-ink)] hover:border-[var(--tenant-primary)]'
                                ].join(' ')}
                            >
                                {language === 'bn' ? c.bn : c.en}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PartsGrid({ parts, cart, onAdd, onDec, language, onJumpToRequest }) {
    if (parts.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-5 md:px-8 pt-10 pb-4 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] mb-4">
                    <Search className="w-6 h-6" />
                </div>
                <h3 className="font-headline font-extrabold text-2xl md:text-3xl tracking-tight text-[var(--tenant-ink)]">
                    {language === 'bn' ? 'কোনো পার্টস মিললো না।' : 'No parts match your search.'}
                </h3>
                <p className="mt-3 text-[var(--tenant-muted)]">
                    {language === 'bn'
                        ? 'এই পার্টটি আমাদের পাইবেন — কাস্টম রিকোয়েস্ট পাঠান।'
                        : "We can still source it for you — send a custom request below and we'll confirm pricing on WhatsApp."}
                </p>
                <button
                    onClick={onJumpToRequest}
                    className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-semibold uppercase tracking-[0.18em] text-xs hover:brightness-110 transition"
                >
                    <Wrench className="w-4 h-4" />
                    {language === 'bn' ? 'কাস্টম পার্টস রিকোয়েস্ট' : 'Request a custom part'}
                </button>
            </div>
        );
    }
    return (
        <section className="pt-8 pb-6 md:pt-10 md:pb-8">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {parts.map((p, i) => {
                    const inCart = cart[p.id] || 0;
                    const name = (language === 'bn' && p.name_bn) ? p.name_bn : p.name;
                    const Icon = pickCategoryIcon(p.category);
                    const catLabel = CATEGORY_LABELS[p.category]?.[language === 'bn' ? 'bn' : 'en'] || (p.category || '').toString().replace(/[_-]/g, ' ');
                    return (
                        <RevealOnScroll key={p.id} delay={Math.min(i * 0.025, 0.3)}>
                            <motion.div
                                whileHover={{ y: -3 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                                className="group relative h-full rounded-xl bg-[var(--tenant-surface-alt)] border border-[var(--tenant-border)] hover:border-[var(--tenant-primary)]/40 hover:shadow-[0_18px_40px_-26px_var(--tenant-primary)] transition-all duration-300 overflow-hidden flex"
                            >
                                {/* Accent stripe */}
                                <div className="w-1 bg-[var(--tenant-primary)] group-hover:w-1.5 transition-all duration-300" />
                                <div className="flex-1 p-4 flex flex-col">
                                    {/* Top row: category icon + genuine */}
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] group-hover:bg-[var(--tenant-primary)] group-hover:text-[var(--tenant-surface)] transition-colors">
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        {p.is_genuine && (
                                            <span title="Genuine" className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] border border-[var(--tenant-primary)]/25 text-[9px] uppercase tracking-[0.18em] font-semibold">
                                                <BadgeCheck className="w-3 h-3" /> Genuine
                                            </span>
                                        )}
                                    </div>

                                    {/* Brand / category meta */}
                                    <div className="mt-3 flex items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[var(--tenant-muted)] truncate">
                                        {p.brand && <span className="font-semibold text-[var(--tenant-ink)]/70">{p.brand}</span>}
                                        {p.brand && <span className="text-[var(--tenant-border)]">·</span>}
                                        <span>{catLabel}</span>
                                    </div>

                                    {/* Name */}
                                    <h3 className={['mt-2 font-headline font-medium text-base md:text-xl tracking-tight text-[var(--tenant-ink)] leading-snug min-h-[2.6em] break-words', language === 'bn' && p.name_bn ? 'font-bn' : ''].join(' ')}>
                                        {name}
                                    </h3>

                                    {/* SKU */}
                                    {p.sku && (
                                        <div className="mt-1 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--tenant-muted)] truncate">
                                            {p.sku}
                                        </div>
                                    )}

                                    {/* Price + cart */}
                                    <div className="mt-auto pt-4 flex items-end justify-between gap-2">
                                        {partPrice(p) > 0 ? (
                                            <div className="min-w-0">
                                                <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{language === 'bn' ? 'রেঞ্জ' : 'Range'}</div>
                                                <div className="font-headline font-semibold text-base md:text-lg text-[var(--tenant-ink)] tracking-tight leading-none mt-0.5 whitespace-nowrap">
                                                    {priceRangeText(p, language)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-[var(--tenant-muted)]">
                                                {language === 'bn' ? 'দাম জানতে ইনকোয়ারি' : 'Ask for price'}
                                            </div>
                                        )}
                                        {inCart === 0 ? (
                                            <button
                                                onClick={() => onAdd(p.id)}
                                                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] text-[11px] uppercase tracking-[0.16em] font-semibold hover:brightness-110 transition"
                                            >
                                                <Plus className="w-3 h-3" />
                                                {language === 'bn' ? 'যোগ' : 'Add'}
                                            </button>
                                        ) : (
                                            <div className="inline-flex items-center gap-1 h-8 rounded-full border border-[var(--tenant-primary)] text-[var(--tenant-primary)] overflow-hidden">
                                                <button onClick={() => onDec(p.id)} className="px-2 h-full hover:bg-[var(--tenant-primary)]/10"><Minus className="w-3 h-3" /></button>
                                                <span className="px-1.5 font-semibold text-xs min-w-[20px] text-center">{inCart}</span>
                                                <button onClick={() => onAdd(p.id)} className="px-2 h-full hover:bg-[var(--tenant-primary)]/10"><Plus className="w-3 h-3" /></button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </RevealOnScroll>
                    );
                })}
            </div>
        </section>
    );
}

function CartDrawer({ open, onClose, items, total, onAdd, onDec, onClear, whatsapp, buildMessage, language }) {
    const waUrl = whatsapp
        ? `https://wa.me/${String(whatsapp).replace(/\D/g, '')}?text=${buildMessage()}`
        : null;
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />
                    <motion.aside
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 280 }}
                        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-[var(--tenant-surface)] border-l border-[var(--tenant-border)] z-50 flex flex-col"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-[var(--tenant-border)]">
                            <div className="flex items-center gap-2">
                                <ShoppingBag className="w-5 h-5 text-[var(--tenant-primary)]" />
                                <h2 className="font-display text-2xl tracking-wide text-[var(--tenant-ink)]">
                                    {language === 'bn' ? 'ইনকোয়ারি' : 'Inquiry'}
                                </h2>
                            </div>
                            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[var(--tenant-surface-alt)] flex items-center justify-center text-[var(--tenant-muted)]">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-3">
                            {items.length === 0 && (
                                <div className="text-center text-[var(--tenant-muted)] py-12">
                                    {language === 'bn' ? 'এখনো কিছু যোগ করেননি।' : 'Nothing added yet.'}
                                </div>
                            )}
                            {items.map(({ part, qty }) => {
                                const name = (language === 'bn' && part.name_bn) ? part.name_bn : part.name;
                                const Icon = pickCategoryIcon(part.category);
                                const unit = partPrice(part);
                                const lineTotal = unit * qty;
                                return (
                                    <div key={part.id} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--tenant-border)]">
                                        <div className="w-12 h-12 rounded-lg bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] flex-shrink-0 flex items-center justify-center">
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm md:text-base font-semibold text-[var(--tenant-ink)] truncate">{name}</div>
                                            {unit > 0 ? (
                                                <div className="text-xs text-[var(--tenant-muted)] flex items-center gap-1.5 mt-0.5">
                                                    <span className="text-[var(--tenant-ink)]/75">{priceRangeText(part, language)} × {qty}</span>
                                                </div>
                                            ) : (
                                                <div className="text-xs text-[var(--tenant-muted)] mt-0.5">{language === 'bn' ? 'WhatsApp-এ দাম জানানো হবে' : 'Price on inquiry'}</div>
                                            )}
                                        </div>
                                        <div className="inline-flex items-center gap-1 h-8 rounded-full border border-[var(--tenant-border)] overflow-hidden">
                                            <button onClick={() => onDec(part.id)} className="px-2 h-full hover:bg-[var(--tenant-surface-alt)]"><Minus className="w-3 h-3" /></button>
                                            <span className="px-2 text-sm font-semibold min-w-[24px] text-center text-[var(--tenant-ink)]">{qty}</span>
                                            <button onClick={() => onAdd(part.id)} className="px-2 h-full hover:bg-[var(--tenant-surface-alt)]"><Plus className="w-3 h-3" /></button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {items.length > 0 && (
                            <div className="border-t border-[var(--tenant-border)] p-5 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">
                                        {language === 'bn' ? 'আনুমানিক মোট' : 'Estimated total'}
                                    </span>
                                    <span className="font-headline font-extrabold text-xl md:text-2xl text-[var(--tenant-primary)] tracking-tight whitespace-nowrap">
                                        {(() => {
                                            const v = total;
                                            const spread = v < 500 ? 0.10 : v < 2000 ? 0.07 : v < 10000 ? 0.05 : 0.03;
                                            const step = v < 1000 ? 25 : v < 5000 ? 50 : v < 20000 ? 100 : 250;
                                            const round = (n) => Math.max(step, Math.round(n / step) * step);
                                            const lo = round(v * (1 - spread));
                                            const hi = round(v * (1 + spread));
                                            return lo === hi ? formatCurrency(lo, language) : `${formatCurrency(lo, language)} – ${formatCurrency(hi, language)}`;
                                        })()}
                                    </span>
                                </div>
                                <p className="text-[11px] text-[var(--tenant-muted)] leading-relaxed">
                                    {language === 'bn'
                                        ? 'চূড়ান্ত মূল্য ও স্টক WhatsApp-এ নিশ্চিত করা হবে।'
                                        : 'Final price & stock confirmed on WhatsApp.'}
                                </p>
                                <a
                                    href={waUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={[
                                        'w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] text-white font-semibold uppercase tracking-wide text-sm',
                                        !waUrl && 'opacity-50 pointer-events-none'
                                    ].join(' ')}
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {language === 'bn' ? 'WhatsApp-এ পাঠান' : 'Send via WhatsApp'}
                                </a>
                                <button onClick={onClear} className="w-full text-xs text-[var(--tenant-muted)] hover:text-[var(--tenant-ink)] py-1">
                                    {language === 'bn' ? 'ক্লিয়ার করুন' : 'Clear inquiry'}
                                </button>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}

// ---------------------------------------------------------------------------
// Custom Parts Request — for parts not in our catalog
// ---------------------------------------------------------------------------
function CustomPartsRequest({ slug, language }) {
    const [form, setForm] = useState({ customer_name: '', customer_phone: '', part_name: '', bike_make: '', bike_model: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const canSubmit = form.customer_name.trim() && form.customer_phone.trim().length >= 10 && form.part_name.trim();

    async function submit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`${API}/tenants/${slug}/parts-request`, form);
            setDone(true);
            setForm({ customer_name: '', customer_phone: '', part_name: '', bike_make: '', bike_model: '', notes: '' });
        } catch (err) {
            setError(language === 'bn' ? 'অনুরোধ পাঠাতে সমস্যা হয়েছে।' : 'Could not submit request. Try again.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section id="parts-request" className="relative py-12 md:py-16 overflow-hidden border-t border-[var(--tenant-border)]">
            <AmbientBlob className="-top-20 -right-20 w-[480px] h-[480px]" tone="primary" />
            <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
                <RevealOnScroll>
                    <div className="text-[10px] uppercase tracking-[0.42em] text-[var(--tenant-primary)] font-semibold">
                        {language === 'bn' ? 'বিশেষ অনুরোধ' : 'Custom Request'}
                    </div>
                    <h2 className="mt-4 font-headline font-extrabold text-3xl md:text-4xl tracking-tight text-[var(--tenant-ink)] leading-tight">
                        {language === 'bn'
                            ? 'খুঁজে পাচ্ছেন না? আমাদের বলুন।'
                            : "Can't find your part? Tell us."}
                    </h2>
                    <p className="mt-4 text-[var(--tenant-muted)] leading-relaxed">
                        {language === 'bn'
                            ? 'রেয়ার বা স্পেসিফিক পার্ট দরকার? আপনার বাইক ও পার্টের নাম পাঠান — আমরা সোর্স করে WhatsApp-এ দাম জানাব।'
                            : 'Need a rare or specific part? Send us the part name and your bike — we’ll source it and confirm pricing on WhatsApp.'}
                    </p>
                    <ul className="mt-6 space-y-2 text-sm text-[var(--tenant-muted)]">
                        {[
                            language === 'bn' ? 'যেকোনো বাইক মডেলের জন্য' : 'Any bike model',
                            language === 'bn' ? 'অরিজিনাল ও আফটারমার্কেট অপশন' : 'OEM & aftermarket options',
                            language === 'bn' ? 'WhatsApp-এ দ্রুত উত্তর' : 'Quick reply on WhatsApp',
                        ].map(t => (
                            <li key={t} className="flex items-center gap-2">
                                <BadgeCheck className="w-4 h-4 text-[var(--tenant-primary)]" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </RevealOnScroll>

                <RevealOnScroll delay={0.1}>
                    <GlassCard className="p-6 md:p-7">
                        {done ? (
                            <div className="text-center py-10">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--tenant-primary)]/15 text-[var(--tenant-primary)] mb-4">
                                    <BadgeCheck className="w-7 h-7" />
                                </div>
                                <h3 className="font-headline font-extrabold text-xl text-[var(--tenant-ink)] mb-2">
                                    {language === 'bn' ? 'অনুরোধ পেয়েছি!' : 'Request received!'}
                                </h3>
                                <p className="text-sm text-[var(--tenant-muted)]">
                                    {language === 'bn' ? 'আমরা শীঘ্রই WhatsApp-এ যোগাযোগ করব।' : "We'll get back to you on WhatsApp shortly."}
                                </p>
                                <button onClick={() => setDone(false)} className="mt-5 text-xs uppercase tracking-[0.24em] text-[var(--tenant-primary)] font-semibold">
                                    {language === 'bn' ? 'আরেকটি অনুরোধ' : 'Submit another'}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <Field
                                        label={language === 'bn' ? 'নাম *' : 'Your name *'}
                                        value={form.customer_name}
                                        onChange={v => setForm(f => ({ ...f, customer_name: v }))}
                                    />
                                    <Field
                                        label={language === 'bn' ? 'ফোন *' : 'Phone *'}
                                        type="tel"
                                        value={form.customer_phone}
                                        onChange={v => setForm(f => ({ ...f, customer_phone: v }))}
                                    />
                                </div>
                                <Field
                                    label={language === 'bn' ? 'পার্টের নাম *' : 'Part name *'}
                                    value={form.part_name}
                                    onChange={v => setForm(f => ({ ...f, part_name: v }))}
                                    placeholder={language === 'bn' ? 'যেমন: Front fork seal' : 'e.g. Front fork seal'}
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <Field
                                        label={language === 'bn' ? 'বাইক ব্র্যান্ড' : 'Bike make'}
                                        value={form.bike_make}
                                        onChange={v => setForm(f => ({ ...f, bike_make: v }))}
                                        placeholder="Honda"
                                    />
                                    <Field
                                        label={language === 'bn' ? 'মডেল' : 'Model'}
                                        value={form.bike_model}
                                        onChange={v => setForm(f => ({ ...f, bike_model: v }))}
                                        placeholder="CB Shine"
                                    />
                                </div>
                                <Field
                                    label={language === 'bn' ? 'অতিরিক্ত নোট' : 'Notes'}
                                    value={form.notes}
                                    onChange={v => setForm(f => ({ ...f, notes: v }))}
                                    textarea
                                />
                                {error && <div className="text-xs text-red-600">{error}</div>}
                                <button
                                    type="submit"
                                    disabled={!canSubmit || submitting}
                                    className="w-full h-12 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-semibold uppercase tracking-[0.18em] text-xs disabled:opacity-50 hover:brightness-110 transition"
                                >
                                    {submitting
                                        ? (language === 'bn' ? 'পাঠানো হচ্ছে…' : 'Sending…')
                                        : (language === 'bn' ? 'অনুরোধ পাঠান' : 'Send request')}
                                </button>
                            </form>
                        )}
                    </GlassCard>
                </RevealOnScroll>
            </div>
        </section>
    );
}

function Field({ label, value, onChange, type = 'text', placeholder, textarea }) {
    const Tag = textarea ? 'textarea' : 'input';
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.26em] text-[var(--tenant-muted)] font-semibold mb-1.5">{label}</div>
            <Tag
                type={type}
                value={value}
                rows={textarea ? 2 : undefined}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                className="w-full bg-[var(--tenant-surface-alt)] border border-[var(--tenant-border)] focus:border-[var(--tenant-primary)] outline-none rounded-xl px-3 py-2.5 text-sm text-[var(--tenant-ink)] placeholder:text-[var(--tenant-muted)] transition"
            />
        </label>
    );
}

// ---------------------------------------------------------------------------
// CustomPartsPill — fixed right-edge vertical rail that scrolls users to the
// "Can't find your part? Tell us." section. Hidden when that section is in view.
// ---------------------------------------------------------------------------
function CustomPartsPill({ language }) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const target = document.getElementById('parts-request');
        if (!target) return;
        const obs = new IntersectionObserver(([entry]) => {
            // Hide pill once the request section is on-screen
            setVisible(!entry.isIntersecting);
        }, { threshold: 0.1 });
        obs.observe(target);
        return () => obs.disconnect();
    }, []);

    function jump() {
        const el = document.getElementById('parts-request');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return (
        <AnimatePresence>
            {visible && (
                <motion.button
                    key="custom-parts-pill"
                    onClick={jump}
                    initial={{ x: 80, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 80, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                    aria-label={language === 'bn' ? 'কাস্টম পার্টস রিকোয়েস্ট' : 'Custom parts request'}
                    className="hidden md:flex fixed right-0 top-[58%] z-30 items-center gap-2.5 pl-5 pr-4 py-3.5 rounded-l-full border border-[var(--tenant-primary)]/40 border-r-0 bg-[var(--tenant-surface)]/95 backdrop-blur-sm text-[var(--tenant-primary)] hover:bg-[var(--tenant-primary)] hover:text-[var(--tenant-surface)] shadow-[0_10px_30px_-12px_var(--tenant-primary)] transition-all duration-300"
                >
                    <Wrench className="w-4 h-4 -rotate-12" />
                    <span className="font-headline font-bold text-base tracking-[0.06em] whitespace-nowrap">
                        {language === 'bn' ? 'কাস্টম পার্টস' : 'Custom Part'}
                    </span>
                </motion.button>
            )}
            {visible && (
                <motion.button
                    key="custom-parts-pill-mobile"
                    onClick={jump}
                    initial={{ y: 80, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 80, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                    aria-label={language === 'bn' ? 'কাস্টম পার্টস রিকোয়েস্ট' : 'Custom parts request'}
                    className="md:hidden fixed bottom-44 right-5 z-30 inline-flex items-center gap-2 h-11 px-4 rounded-full border border-[var(--tenant-primary)]/40 bg-[var(--tenant-surface)]/95 backdrop-blur-sm text-[var(--tenant-primary)] shadow-[0_10px_30px_-12px_var(--tenant-primary)]"
                >
                    <Wrench className="w-4 h-4" />
                    <span className="font-headline font-bold text-[13px] tracking-[0.06em]">
                        {language === 'bn' ? 'কাস্টম' : 'Custom'}
                    </span>
                </motion.button>
            )}
        </AnimatePresence>
    );
}

function Loading({ slug }) {
    return <TenantLoader slug={slug} label="Parts Counter" />;
}

function NotFound({ slug }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8f6f0] text-[#0c1a12] px-6">
            <div className="text-center max-w-md">
                <AlertTriangle className="w-12 h-12 mx-auto text-amber-600 mb-4" />
                <h1 className="font-display text-4xl tracking-wider">Workshop not found</h1>
                <p className="mt-3 text-[#0c1a12]/60 text-sm">No garage at <span className="font-mono">/{slug}</span>.</p>
                <Link to="/" className="inline-block mt-6 px-5 py-2 rounded-full bg-[#0c1a12] text-white font-semibold text-sm uppercase tracking-wide hover:brightness-110">Home</Link>
            </div>
        </div>
    );
}
